import { useEffect, useRef, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import type { SnackbarCloseReason, SnackbarProps } from '@mui/material/Snackbar';
import AlertTitle from '@mui/material/AlertTitle';
import { alertClasses } from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { Alert } from '../Alert';
import type { AlertProps } from '../Alert';

export interface ToastProps {
  /** Controla a visibilidade, no mesmo espírito do `open` do `Snackbar`. */
  open: boolean;
  /** Texto exibido dentro do `Componente_Alert` interno. */
  message: string;
  /** Título em negrito acima da mensagem, como o título do toastr legado. */
  title?: string;
  /** Severidade do `Componente_Alert` interno. */
  severity?: AlertProps['severity'];
  onClose: () => void;
  /** `top-right` é o default, o mesmo `toast-top-right` do toastr legado fora das rotas de login. */
  anchorOrigin?: SnackbarProps['anchorOrigin'];
  /**
   * Com valor, o toast se fecha sozinho e exibe a barra de progresso; o tempo pausa enquanto o
   * ponteiro está sobre ele. Sem valor, fica aberto até um clique nele ou a tecla Esc.
   */
  autoHideDuration?: number;
}

const ANCORAGEM_DEFAULT: SnackbarProps['anchorOrigin'] = {
  vertical: 'top',
  horizontal: 'right',
};

// Fundos do `.toast-*` do toastr legado, para o aviso ter a mesma cara nas duas metades da
// aplicação. Ficam só no Toast, e não no tema: com texto branco, `success` (3,1:1), `info`
// (3,4:1) e `warning` (2,3:1) ficam abaixo do contraste AA, exceção aceita só neste componente.
const CORES_DO_TOASTR_LEGADO: Record<NonNullable<AlertProps['severity']>, string> = {
  success: '#51A351',
  error: '#BD362F',
  info: '#2F96B4',
  warning: '#F89406',
};

const ESVAZIAR = keyframes`
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
`;

// O Snackbar do MUI afasta o toast 24px da borda a partir de `sm`; o toastr legado usa 12px.
// Só o lado ancorado recebe o deslocamento, para não fixar `top` num toast ancorado embaixo.
function afastamentoDaBorda(ancoragem: NonNullable<SnackbarProps['anchorOrigin']>): SxProps<Theme> {
  return (theme) => {
    const afastamento = { sm: theme.spacing(1.5) };
    return {
      [ancoragem.vertical]: afastamento,
      ...(ancoragem.horizontal === 'center' ? null : { [ancoragem.horizontal]: afastamento }),
    };
  };
}

/**
 * Toast do design system: `Snackbar` + `Componente_Alert` preenchido, com a aparência do toastr
 * do Angular legado (canto superior direito, fundo sólido, título, barra de progresso e
 * fechamento por clique), inclusive nas cores de fundo de cada severidade.
 */
export function Toast({
  open,
  message,
  title,
  severity = 'error',
  onClose,
  anchorOrigin = ANCORAGEM_DEFAULT,
  autoHideDuration,
}: ToastProps): JSX.Element {
  const [pausado, definirPausado] = useState(false);
  const tempoRestante = useRef(autoHideDuration);

  // Um conteúdo novo recomeça a contagem. Declarado antes do efeito do timer: a limpeza do timer
  // anterior desconta o tempo decorrido, e só depois disso o restante volta ao total.
  useEffect(() => {
    tempoRestante.current = autoHideDuration;
    definirPausado(false);
  }, [open, message, severity, autoHideDuration]);

  // Timer próprio, e não o `autoHideDuration` do Snackbar: o Snackbar retoma com metade do tempo
  // após a pausa, o que dessincronizaria a barra de progresso, que pausa e retoma do ponto exato.
  useEffect(() => {
    if (!open || tempoRestante.current === undefined || pausado) {
      return undefined;
    }
    const inicio = Date.now();
    const timer = setTimeout(() => onClose(), tempoRestante.current);
    return () => {
      clearTimeout(timer);
      if (tempoRestante.current !== undefined) {
        tempoRestante.current -= Date.now() - inicio;
      }
    };
  }, [open, pausado, onClose, message, severity, autoHideDuration]);

  // Clique fora não fecha, como no toastr legado. O Esc continua fechando, o que mantém um
  // caminho de teclado para o toast sem tempo de fechamento.
  const aoFecharSnackbar = (_evento: unknown, motivo: SnackbarCloseReason): void => {
    if (motivo !== 'clickaway') {
      onClose();
    }
  };

  const estiloDoAlert: SxProps<Theme> = (theme) => ({
    position: 'relative',
    overflow: 'hidden',
    width: { xs: '100%', sm: theme.spacing(37.5) },
    cursor: 'pointer',
    boxShadow: `0 0 ${theme.spacing(1.5)} ${theme.palette.grey[500]}`,
    '&:hover': { boxShadow: `0 0 ${theme.spacing(1.5)} ${theme.palette.common.black}` },
    backgroundColor: CORES_DO_TOASTR_LEGADO[severity],
    // Branco em todas as severidades, como no legado. Fixado aqui porque o MUI deriva a cor do
    // texto da cor do tema, e não do fundo aplicado acima.
    color: theme.palette.common.white,
    [`& .${alertClasses.icon}`]: { color: 'inherit' },
  });

  return (
    <Snackbar
      open={open}
      onClose={aoFecharSnackbar}
      anchorOrigin={anchorOrigin}
      sx={afastamentoDaBorda(anchorOrigin)}
    >
      <Alert
        severity={severity}
        variant="filled"
        // O `Alert` também repassa o evento ao handler; o contrato público do Toast é sem argumentos.
        onClick={() => onClose()}
        onMouseEnter={() => definirPausado(true)}
        onMouseLeave={() => definirPausado(false)}
        sx={estiloDoAlert}
      >
        {title && <AlertTitle sx={{ fontWeight: 'fontWeightBold' }}>{title}</AlertTitle>}
        {message}
        {autoHideDuration !== undefined && (
          <Box
            // Recomeça a animação junto com a contagem quando o conteúdo muda.
            key={`${message}|${severity}|${autoHideDuration}`}
            data-testid="toast-progress"
            aria-hidden
            sx={(theme) => ({
              position: 'absolute',
              left: 0,
              bottom: 0,
              width: '100%',
              height: theme.spacing(0.5),
              bgcolor: 'common.black',
              opacity: 0.4,
              transformOrigin: 'left',
              animation: `${ESVAZIAR} ${autoHideDuration}ms linear forwards`,
              animationPlayState: pausado ? 'paused' : 'running',
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            })}
          />
        )}
      </Alert>
    </Snackbar>
  );
}
